import { FunctionComponent, ReactElement } from "react";

import { Route, Routes } from "react-router-dom";

import { RequireAuth } from "./components/common";
import { PrimaryLayout } from "./components/layout";
import {
    AcceptInvitationScreen,
    AdminGroupScreen,
    AmazonEtdResultsScreen,
    BulkOrderScreen,
    BulkOrdersScreen,
    CartsScreen,
    CollectionsScreen,
    CustomerGroupScreen,
    CustomerGroupsScreen,
    CustomerReviewsScreen,
    CustomizedOrdersScreen,
    DiscountScreen,
    DiscountsScreen,
    EditEntitySchemaScreen,
    EditLinkScreen,
    EditProductVariantScreen,
    FreebieRuleScreen,
    FreebieRulesScreen,
    IngredientCategoriesScreen,
    IngredientScreen,
    IngredientsScreen,
    InstaMartResultsScreen,
    Login,
    OrderScreen,
    OrdersScreen,
    ProductCollectionScreen,
    RecipeScreen,
    UnmatchedIngredientsScreen,
    VariantStockHistoryScreen,
    ViewAdminGroupsScreen,
    ViewCustomerScreen,
    ViewCustomersScreen,
    ViewEntitySchemasScreen,
    ViewLinkScreen,
    ViewLinksScreen,
    ViewProductScreen,
    ViewProductsScreen,
    ViewRecipesScreen,
    PriceChangeSchedulesScreen,
    PriceChangeScheduleDetailScreen,
} from "./screens";
import { CreateOrderScreen } from "./screens/order/create/CreateOrder";
import { SelectCart } from "./screens/order/create/SelectCart";
import { ViewUsersScreen } from "./screens/users";
import { useAuth } from "./sdk/hooks";
import { AdminRole } from "./utils/enums";

const App: FunctionComponent = (): ReactElement => {
    const { user } = useAuth();

    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route
                path="/invitations/accept"
                element={<AcceptInvitationScreen />}
            />
            <Route
                path="/"
                element={
                    <RequireAuth>
                        <PrimaryLayout />
                    </RequireAuth>
                }
            >
                <Route path="/links" element={<ViewLinksScreen />} />

                <Route path="/links/:linkId" element={<ViewLinkScreen />} />
                <Route
                    path="/links/:linkId/edit"
                    element={<EditLinkScreen />}
                />

                <Route path="/orders" element={<OrdersScreen />} />
                <Route
                    path="customized-orders"
                    element={<CustomizedOrdersScreen />}
                />
                <Route path="/orders/:id" element={<OrderScreen />} />
                <Route path="/customer-carts" element={<CartsScreen />} />
                <Route path="/carts/new" element={<SelectCart />} />
                <Route path="/carts/:id" element={<CreateOrderScreen />} />

                <Route path="/discounts" element={<DiscountsScreen />} />
                <Route path="/discounts/:id" element={<DiscountScreen />} />
                <Route path="/freebie-rules" element={<FreebieRulesScreen />} />
                <Route
                    path="/freebie-rules/:id"
                    element={<FreebieRuleScreen />}
                />
                <Route
                    path="/product-collections"
                    element={<CollectionsScreen />}
                />
                <Route
                    path="/product-collections/:id"
                    element={<ProductCollectionScreen />}
                />
                <Route path="/recipes" element={<ViewRecipesScreen />} />
                <Route path="/recipes/:id" element={<RecipeScreen />} />
                <Route path="/products/:id" element={<ViewProductScreen />} />
                <Route
                    path="/products/:productId/variants/:variantId/edit"
                    element={<EditProductVariantScreen />}
                />
                <Route
                    path="/inventory/variants/:variantId/history"
                    element={<VariantStockHistoryScreen />}
                />
                <Route
                    path="/customer-groups"
                    element={<CustomerGroupsScreen />}
                />
                <Route
                    path="/customer-groups/:id"
                    element={<CustomerGroupScreen />}
                />

                <Route
                    path="/admin-groups"
                    element={<ViewAdminGroupsScreen />}
                />

                <Route
                    path="/admin-groups/:id"
                    element={<AdminGroupScreen />}
                />
                <Route
                    path="/customer-reviews"
                    element={<CustomerReviewsScreen />}
                />

                <Route path="/customers" element={<ViewCustomersScreen />} />

                <Route path="/ingredients" element={<IngredientsScreen />} />
                <Route
                    path="/ingredients/unmatched"
                    element={<UnmatchedIngredientsScreen />}
                />
                <Route path="/ingredients/:id" element={<IngredientScreen />} />
                <Route
                    path="/ingredient-categories"
                    element={<IngredientCategoriesScreen />}
                />

                <Route
                    path="/customers/:customerId"
                    element={<ViewCustomerScreen />}
                />
                <Route
                    path="/entity-schemas"
                    element={<ViewEntitySchemasScreen />}
                />
                <Route
                    path="/entity-schemas/:id"
                    element={<EditEntitySchemaScreen />}
                />
                <Route path="/bulk-orders" element={<BulkOrdersScreen />} />
                <Route
                    path="/bulk-orders/:bulkOrderId"
                    element={<BulkOrderScreen />}
                />
                <Route
                    path="/amazon-etd"
                    element={<AmazonEtdResultsScreen />}
                />
                <Route path="/instamart" element={<InstaMartResultsScreen />} />
                {user &&
                    [
                        AdminRole.ROOT,
                        AdminRole.ADMIN,
                        AdminRole.MANAGER,
                    ].includes(user.role) && (
                        <Route path="/users" element={<ViewUsersScreen />} />
                    )}
                <Route
                    path="/price-schedules"
                    element={<PriceChangeSchedulesScreen />}
                />
                <Route
                    path="/price-schedules/:id"
                    element={<PriceChangeScheduleDetailScreen />}
                />
                <Route path="/products" element={<ViewProductsScreen />} />
            </Route>
        </Routes>
    );
};

export default App;

// dummy commit
// another dummy commit
